/**
 * Skills, with their tier surfaced — `Survival · Competent · 4/10`.
 *
 * `4/10` alone asks the player to hold the engine's ladder in their head: they have to know
 * that 4 is where "novice" ends, and nothing on screen has ever told them. `tier_summary` is
 * the API's own word for the *current* level, already localized, and putting it between the
 * name and the number turns a coordinate into a statement.
 *
 * Abilities expand rather than list, because most skills have none and the ones that do have
 * one or two — a permanently-expanded list would be four blank rows and a full one.
 */
import type { Skill } from '@/api/generated';
import { useT } from '@/i18n/context';
import { Nothing } from '@/ui/Section';

export function Skills({ skills }: { skills: readonly Skill[] }) {
  const t = useT();

  if (skills.length === 0) return <Nothing>{t('ui.panel.nothing_learned')}</Nothing>;

  return (
    <ul className="flex flex-col">
      {skills.map((skill) => (
        <li key={skill.name}>
          <SkillRow skill={skill} />
        </li>
      ))}
    </ul>
  );
}

function SkillRow({ skill }: { skill: Skill }) {
  const line = (
    <>
      <span className="text-fg-muted">{skill.display_name}</span>
      <span className="text-fg-faint">·</span>
      <span className="text-fg-faint truncate">{skill.tier_summary}</span>
      <span className="text-fg ml-auto shrink-0 tabular-nums">{skill.level}/10</span>
    </>
  );

  if (skill.abilities.length === 0) {
    return (
      <div
        className="flex items-baseline gap-2 py-1 font-mono"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {line}
      </div>
    );
  }

  return (
    <details>
      <summary
        className="hover:text-fg flex cursor-pointer list-none items-baseline gap-2 py-1 font-mono transition-colors"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {line}
      </summary>
      <ul className="border-rule mt-1 mb-2 ml-1 flex flex-col gap-2 border-l pl-3">
        {skill.abilities.map((ability) => (
          <li key={ability.key}>
            <p className="text-fg-muted font-mono" style={{ fontSize: 'var(--text-meta)' }}>
              {ability.display_name}
            </p>
            <p className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
              {ability.description}
            </p>
          </li>
        ))}
      </ul>
    </details>
  );
}
